import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { buildAppMeta, SITE_URL } from "@/lib/seo";
import { publicClipUrl } from "@/lib/invoicing";
import { useEffect, useMemo, useRef, useState } from "react";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/AppShell";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  ArrowLeft,
  Eye,
  Home,
  Monitor,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  Send,
  Smartphone,
  Sparkles,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { render as renderEmail } from "@react-email/components";
import { createElement } from "react";
import { template as lessonRecapTemplate } from "@/lib/email-templates/lesson-recap";
import { type SessionClip } from "@/components/sessions/SessionClipsCard";
import { SessionPolicyActions } from "@/components/sessions/SessionPolicyActions";
import { discardRecap, undoDiscardRecap } from "@/lib/recaps";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { reverseCreditForAttendee, type GroupCreditPolicy } from "@/lib/credits";
import { BillLessonDialog } from "@/components/sessions/BillLessonDialog";
import { Receipt } from "lucide-react";

type Attendee = {
  id: string;
  coach_student_id: string;
  student_name: string;
  student_email: string | null;
  attended: boolean;
  per_attendee_note: string;
  recap_sent_at: string | null;
};

const CLIP_SIGN_TTL = 60 * 60 * 24 * 90; // 90 days

export const Route = createFileRoute("/app/sessions/$sessionId")({
  head: ({ params }) => ({
    meta: buildAppMeta({
      title: "Session details — CoachQuick",
      description: "Session notes, drills covered, attached media, and the automated recap sent to your client.",
      path: `/app/sessions/${params.sessionId}`,
    }),
    links: [{ rel: "canonical", href: `${SITE_URL}/app/sessions/${params.sessionId}` }],
  }),
  component: () => (
    <RequireAuth role="coach">
      <SessionDetail />
    </RequireAuth>
  ),
});

type DrillItem = {
  id: string;
  title: string;
  completed: boolean;
  is_homework: boolean;
  video_url: string | null;
  body_text: string | null;
  item_type: "technique" | "drill" | "recommendation" | "resource";
  parent_title: string | null;
  image_url: string | null;
};

function firstNameOf(name?: string | null) {
  return (name ?? "").trim().split(/\s+/)[0] || "client";
}

function fmtLongDate(s: string | null | undefined) {
  if (!s) return "";
  const [y, m, d] = s.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return s;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

function SessionDetail() {
  const { sessionId } = Route.useParams();
  const { user } = useAuth();
  const [session, setSession] = useState<any>(null);
  const [client, setClient] = useState<any>(null);
  const [items, setItems] = useState<DrillItem[]>([]);
  const [clips, setClips] = useState<SessionClip[]>([]);
  const [coachProfile, setCoachProfile] = useState<any>(null);
  const [summary, setSummary] = useState<{
    id?: string;
    ai_draft?: string;
    final_text?: string;
    sent_at?: string | null;
  } | null>(null);
  const [draftText, setDraftText] = useState("");
  const [editingDraft, setEditingDraft] = useState(false);
  const [attendees, setAttendees] = useState<Attendee[]>([]);
  const [sendIndividualRecaps, setSendIndividualRecaps] = useState(true);
  const [sessionNotes, setSessionNotes] = useState("");
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<"desktop" | "mobile">("desktop");
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [billDialogOpen, setBillDialogOpen] = useState(false);
  const [isBilled, setIsBilled] = useState(false);
  const [policyEvent, setPolicyEvent] = useState<{
    kind: string;
    note: string | null;
    created_at: string;
  } | null>(null);
  const autoGenAttempted = useRef(false);
  const navigate = useNavigate();

  async function handleDiscard() {
    setConfirmDiscard(false);
    try {
      await discardRecap(sessionId);
      toast("Recap discarded", {
        description: "The lesson stays in your records.",
        duration: 6000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await undoDiscardRecap(sessionId);
              toast.success("Recap restored");
            } catch {
              toast.error("Couldn't restore the recap");
            }
          },
        },
      });
      navigate({ to: "/app/queue" });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't discard recap");
    }
  }

  const firstName = firstNameOf(client?.student_name);

  const subject = useMemo(() => {
    const name = coachProfile?.full_name ?? user?.user_metadata?.full_name ?? "your coach";
    const date = fmtLongDate(session?.session_date);
    return `Lesson recap from ${name}${date ? ` — ${date}` : ""}`;
  }, [coachProfile, user, session]);

  const homeworkCount = items.filter((i) => i.is_homework).length;
  const drillCount = items.filter((i) => !i.is_homework).length;

  async function load() {
    const { data: s } = await supabase
      .from("sessions")
      .select("*")
      .eq("id", sessionId)
      .maybeSingle();
    setSession(s);
    setSessionNotes((s as any)?.notes ?? "");
    setSendIndividualRecaps((s as any)?.send_individual_recaps ?? true);
    if (s) {
      const { data: c } = await supabase
        .from("coach_students")
        .select("*")
        .eq("id", (s as any).coach_student_id)
        .maybeSingle();
      setClient(c);
      const { data: sd } = await supabase
        .from("session_drills")
        .select(
          "id, completed, is_homework, drills(title, video_url, body_text, item_type, image_url, parent:drills!parent_id(title))",
        )
        .eq("session_id", sessionId);
      setItems(
        (sd ?? []).map((r: any) => ({
          id: r.id,
          title: r.drills?.title ?? "Item",
          completed: r.completed,
          is_homework: r.is_homework,
          video_url: r.drills?.video_url ?? null,
          body_text: r.drills?.body_text ?? null,
          item_type: r.drills?.item_type ?? "drill",
          parent_title: r.drills?.parent?.title ?? null,
          image_url: r.drills?.image_url ?? null,
        })),
      );
      const { data: sum } = await supabase
        .from("summaries")
        .select("*")
        .eq("session_id", sessionId)
        .maybeSingle();
      setSummary(sum as any);
      if (sum) setDraftText(((sum as any).final_text || (sum as any).ai_draft) ?? "");
      if (user) {
        const { data: cp } = await supabase
          .from("coach_profile" as never)
          .select("*")
          .eq("coach_id", user.id)
          .maybeSingle();
        setCoachProfile(cp ?? null);
      }
      const { data: att } = await supabase
        .from("session_attendees")
        .select(
          "id, coach_student_id, attended, per_attendee_note, recap_sent_at, coach_students(student_name, student_email)",
        )
        .eq("session_id", sessionId);
      setAttendees(
        (att ?? []).map((r: any) => ({
          id: r.id,
          coach_student_id: r.coach_student_id,
          student_name: r.coach_students?.student_name ?? "Client",
          student_email: r.coach_students?.student_email ?? null,
          attended: r.attended ?? true,
          per_attendee_note: r.per_attendee_note ?? "",
          recap_sent_at: r.recap_sent_at ?? null,
        })),
      );

      // Compute isBilled: pack credit burned (non-reversed ledger), session_payments row,
      // or any invoice line item references this session.
      const [{ data: ledger }, { data: pay }, { data: inv }] = await Promise.all([
        supabase
          .from("package_ledger")
          .select("id")
          .eq("session_id", sessionId)
          .eq("kind", "consume")
          .is("reversed_by_ledger_id", null)
          .limit(1),
        supabase
          .from("session_payments")
          .select("id")
          .eq("session_id", sessionId)
          .limit(1),
        supabase
          .from("invoice_line_items" as never)
          .select("id")
          .eq("session_id", sessionId)
          .limit(1),
      ]);
      setIsBilled(
        (ledger?.length ?? 0) > 0 || (pay?.length ?? 0) > 0 || (inv?.length ?? 0) > 0,
      );

      const { data: pe } = await supabase
        .from("session_policy_events")
        .select("kind, note, created_at")
        .eq("session_id", sessionId)
        .is("reversed_by_event_id", null)
        .in("kind", ["cancel_in_window", "cancel_out_of_window", "no_show", "reschedule"])
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      setPolicyEvent((pe as any) ?? null);
    }
  }

  useEffect(() => {
    load();
  }, [sessionId]);

  // Load clips (managed on the Log Session screen) so they can be embedded in the recap email.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("session_clips" as never)
        .select("*")
        .eq("session_id", sessionId)
        .order("position", { ascending: true });
      if (!cancelled) setClips(((data as unknown as SessionClip[]) ?? []));
    })();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  // Auto-generate the recap draft on first open when there isn't one yet.
  // Cheap (one short AI call) and saves a click — Regenerate stays available for retries.
  useEffect(() => {
    if (autoGenAttempted.current) return;
    if (!session || !client || !coachProfile) return;
    if (policyEvent) return; // non-lesson event
    if (isBilled) return;
    if (summary?.sent_at) return;
    const existing = (summary?.final_text || summary?.ai_draft || "").trim();
    if (existing) return;
    if (generating) return;
    autoGenAttempted.current = true;
    void generateDraft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, client, coachProfile, summary, policyEvent, isBilled]);


  async function toggleAttendeeAttended(attendeeId: string, attended: boolean) {
    setAttendees((prev) => prev.map((a) => (a.id === attendeeId ? { ...a, attended } : a)));
    const target = attendees.find((a) => a.id === attendeeId);
    const { error } = await supabase
      .from("session_attendees")
      .update({ attended } as never)
      .eq("id", attendeeId);
    if (error) {
      toast.error(`Couldn't update attendance: ${error.message}`);
      return;
    }
    // If session is already sent and we're marking someone as no-show, refund any
    // credit that was previously burned for them. We no longer auto-burn on
    // attendance changes — billing is an explicit step (BillLessonDialog).
    const isSent = session?.status === "sent" || !!summary?.sent_at;
    if (!isSent || !user || !target) return;
    if (!attended) {
      const reversed = await reverseCreditForAttendee({
        coachId: user.id,
        sessionId,
        coachStudentId: target.coach_student_id,
        attendeeId,
        reasonNote: "Marked no-show after send",
      });
      if (reversed) toast.success(`Credit refunded to ${target.student_name}.`);
    }
  }

  async function saveAttendeeNote(attendeeId: string, note: string) {
    const original = attendees.find((a) => a.id === attendeeId);
    if (!original || original.per_attendee_note === note) return;
    const { error } = await supabase
      .from("session_attendees")
      .update({ per_attendee_note: note } as never)
      .eq("id", attendeeId);
    if (error) toast.error(`Couldn't save note: ${error.message}`);
  }

  async function toggleSendIndividualRecaps(next: boolean) {
    setSendIndividualRecaps(next);
    const { error } = await supabase
      .from("sessions")
      .update({ send_individual_recaps: next } as never)
      .eq("id", sessionId);
    if (error) toast.error(`Couldn't save preference: ${error.message}`);
  }

  async function sendAttendeeRecap(attendee: Attendee) {
    if (!attendee.student_email) {
      toast.error(`${attendee.student_name} has no email on file.`);
      return;
    }
    if (!draftText.trim()) {
      toast.error("Write or generate a recap first.");
      return;
    }

    // Billing is handled separately via BillLessonDialog after the recap is sent.
    const body =
      draftText.trim() +
      (attendee.per_attendee_note.trim()
        ? `\n\n— Just for you —\n${attendee.per_attendee_note.trim()}`
        : "");

    const url = `mailto:${encodeURIComponent(attendee.student_email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = url;
    const sentAt = new Date().toISOString();
    const { error } = await supabase
      .from("session_attendees")
      .update({ recap_sent_at: sentAt } as never)
      .eq("id", attendee.id);
    if (error) {
      toast.error(`Couldn't mark sent: ${error.message}`);
      return;
    }
    setAttendees((prev) =>
      prev.map((a) => (a.id === attendee.id ? { ...a, recap_sent_at: sentAt } : a)),
    );
    toast.success(`Opened recap for ${attendee.student_name}.`);
    if (!isBilled) setBillDialogOpen(true);
  }

  async function saveSessionNotes() {
    if (!session) return;
    if (sessionNotes === (session.notes ?? "")) return;
    await supabase
      .from("sessions")
      .update({ notes: sessionNotes } as never)
      .eq("id", sessionId);
    setSession({ ...session, notes: sessionNotes });
  }

  async function generateDraft() {
    if (!session || !client) return;
    setGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-summary", {
        body: {
          studentName: client.student_name,
          sessionDate: session.session_date,
          nextTime: session.notes,
          coachName: coachProfile?.full_name ?? user?.user_metadata?.full_name ?? "your coach",
          coachProfile,
          items: items.map((i) => ({
            title: i.title,
            body_text: i.body_text,
            video_url: i.video_url,
            item_type: i.item_type,
            parent_title: i.parent_title,
          })),
        },
      });
      if (error) throw error;
      const draft = (data as any)?.draft ?? "";
      setDraftText(draft);
      await supabase
        .from("summaries")
        .upsert(
          { session_id: sessionId, ai_draft: draft, final_text: draft } as never,
          { onConflict: "session_id" } as never,
        );
      toast.success("Automated draft ready — edit it before sending");
      load();
    } catch (err: any) {
      const msg = err?.message ?? "Could not generate draft";
      if (msg.includes("429")) toast.error("Rate limited. Try again shortly.");
      else if (msg.includes("402"))
        toast.error("AI credits exhausted. Add credits in Settings → Workspace → Usage.");
      else toast.error(msg);
    } finally {
      setGenerating(false);
    }
  }

  async function saveDraft() {
    await supabase
      .from("summaries")
      .upsert(
        { session_id: sessionId, final_text: draftText } as never,
        { onConflict: "session_id" } as never,
      );
    toast.success("Draft saved");
  }

  async function buildEmailClips() {
    if (!clips.length)
      return [] as {
        posterUrl: string | null;
        videoUrl: string | null;
        caption: string;
        durationSeconds: number | null;
      }[];
    const out = await Promise.all(
      clips.map(async (c) => {
        // For the embedded HTML email, the click target is the short branded
        // redirect URL — that way old emails keep working and we don't ship
        // multi-thousand-character signed URLs to the client.
        const videoUrl = c.share_token
          ? publicClipUrl(c.share_token)
          : c.external_url;
        let posterUrl = c.poster_url;
        // Posters can stay as long-lived signed URLs (small images, embedded
        // inline in the email body — no redirect possible).
        if (c.source === "upload" && c.poster_path) {
          const { data } = await supabase.storage
            .from("session-clips")
            .createSignedUrl(c.poster_path, CLIP_SIGN_TTL);
          if (data?.signedUrl) posterUrl = data.signedUrl;
        }
        return {
          posterUrl,
          videoUrl,
          caption: c.caption || "",
          durationSeconds: c.duration_seconds,
        };
      }),
    );
    return out;
  }

  async function openPreview() {
    if (!session) return;
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const emailClips = await buildEmailClips();
      const html = await renderEmail(
        createElement(lessonRecapTemplate.component, {
          studentName: client?.student_name,
          coachName: user?.user_metadata?.full_name ?? "Your coach",
          sessionDate: fmtLongDate(session.session_date),
          recapText: draftText,
          drills: items.map((i) => ({
            title: i.title,
            isHomework: i.is_homework,
            videoUrl: i.video_url,
            imageUrl: i.image_url,
          })),
          clips: emailClips,
        }),
      );
      setPreviewHtml(html);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to render preview");
      setPreviewOpen(false);
    } finally {
      setPreviewLoading(false);
    }
  }

  function buildPlainTextBody() {
    const lines: string[] = [];
    if (draftText.trim()) lines.push(draftText.trim(), "");
    if (clips.length) {
      lines.push("Clips from your lesson:");
      for (const c of clips) {
        const label = c.caption || (c.source === "upload" ? "Clip" : "Linked clip");
        const url = c.share_token
          ? publicClipUrl(c.share_token)
          : (c.external_url ?? "");
        lines.push(`• ${label}${url ? ` — ${url}` : ""}`);
      }
      if (clips.some((c) => c.source === "upload")) {
        lines.push(
          "(Tip: download any clips you want to keep — they're removed from CoachQuick 90 days after upload.)",
        );
      }
      lines.push("");
    }

    const homework = items.filter((i) => i.is_homework);
    const others = items.filter((i) => !i.is_homework);
    if (others.length) {
      lines.push("What we worked on:");
      for (const i of others) {
        lines.push(`• ${i.title}${i.video_url ? ` — ${i.video_url}` : ""}`);
      }
      lines.push("");
    }
    if (homework.length) {
      lines.push("Homework before next session:");
      for (const i of homework) {
        lines.push(`• ${i.title}${i.video_url ? ` — ${i.video_url}` : ""}`);
      }
      lines.push("");
    }
    // Sign-off and signature are intentionally NOT appended here — the automated draft
    // already includes them (see generate-summary prompt). Adding them again
    // would cause duplicate signatures in the recipient's inbox.
    return lines.join("\n").trimEnd();
  }

  async function markSent() {
    const sentAt = new Date().toISOString();
    await supabase
      .from("summaries")
      .upsert(
        { session_id: sessionId, final_text: draftText, sent_at: sentAt } as never,
        { onConflict: "session_id" } as never,
      );
    await supabase.from("sessions").update({ status: "sent" } as never).eq("id", sessionId);
    load();
  }

  async function emailRecap() {
    if (!client?.student_email) {
      toast.error("This client has no email on file.");
      return;
    }
    if (!draftText.trim()) {
      toast.error("Write or generate a recap first.");
      return;
    }
    setSending(true);
    try {
      const body = buildPlainTextBody();
      const MAILTO_LIMIT = 1800;
      let mailtoBody = body;
      let truncatedNotice = "";
      if (body.length > MAILTO_LIMIT) {
        mailtoBody =
          body.slice(0, MAILTO_LIMIT) +
          "\n\n[Message truncated — full version copied to clipboard. Paste here.]";
        try {
          await navigator.clipboard.writeText(body);
          truncatedNotice = " Full body copied to clipboard — paste it in.";
        } catch {
          // ignore clipboard failures
        }
      }
      const url = `mailto:${encodeURIComponent(client.student_email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(mailtoBody)}`;
      window.location.href = url;
      await markSent();
      toast.success(`Opened in your mail app.${truncatedNotice}`);
      // After the recap goes out, prompt the coach to bill it.
      // (Skippable; can be re-opened from the "Bill this lesson" button.)
      if (!isBilled) setBillDialogOpen(true);
    } finally {
      setSending(false);
    }
  }

  if (!session) {
    return (
      <AppShell>
        <p className="text-sm text-muted-foreground">Loading…</p>
      </AppShell>
    );
  }

  const clientName = client?.student_name ?? "client";
  const isSent = session.status === "sent" || !!summary?.sent_at;
  const hasDraft = !!(summary?.ai_draft || draftText);

  if (policyEvent && client && user) {
    const kindLabel =
      policyEvent.kind === "no_show"
        ? "No-show"
        : policyEvent.kind === "reschedule"
          ? "Rescheduled"
          : "Cancelled";
    return (
      <AppShell>
        <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
          <Link
            to="/app"
            className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to dashboard
          </Link>
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              {kindLabel} · {fmtLongDate(session.session_date)}
            </p>
            <h1 className="mt-2 font-display text-[34px] font-black leading-tight tracking-tight text-foreground md:text-[38px]">
              {clientName}
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              This lesson was marked <span className="font-medium">{kindLabel.toLowerCase()}</span>{" "}
              and is not on the books as a coached session. No recap will be sent.
            </p>
            {policyEvent.note && (
              <p className="mt-3 rounded-md bg-muted/40 p-3 text-sm">{policyEvent.note}</p>
            )}
          </div>

          <div className="mb-4">
            <SessionPolicyActions
              sessionId={sessionId}
              coachId={user.id}
              clientId={client.id}
              sessionDate={session.session_date}
            />
          </div>
        </div>
      </AppShell>
    );
  }





  return (
    <AppShell>
      <div className="-mx-4 -my-6 min-h-[calc(100vh-4rem)] bg-canvas-soft px-4 py-6 md:-mx-8 md:px-8">
        {/* Breadcrumb */}
        {client && (
          <Link
            to="/app/clients/$clientId"
            params={{ clientId: client.id }}
            className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> {clientName}
          </Link>
        )}

        {/* Header */}
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Lesson · {fmtLongDate(session.session_date)}
            </p>
            <h1 className="mt-2 font-display text-[34px] font-black leading-tight tracking-tight text-foreground md:text-[38px]">
              Session with {clientName}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {isSent ? (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#e6f4ea] px-2.5 py-1 text-[11px] font-semibold text-[#054d28] dark:bg-[#054d28]/30 dark:text-[#7ed492]">
                  Sent
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#fff3c2] px-2.5 py-1 text-[11px] font-semibold text-[#b86700] dark:bg-[#b86700]/20 dark:text-[#fcd34d]">
                  Draft · not sent
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={openPreview}
              disabled={!draftText.trim()}
              className="rounded-[10px]"
            >
              <Eye className="h-4 w-4" /> Preview email
            </Button>
            {!(attendees.length >= 2 && sendIndividualRecaps) && (
              <Button
                onClick={emailRecap}
                disabled={sending || !draftText.trim() || !client?.student_email}
                className="rounded-[10px]"
              >
                <Send className="h-4 w-4" />
                {sending ? "Opening…" : `Send recap to ${firstName}`}
              </Button>
            )}
            {isSent && !isBilled && client && (
              <Button
                variant="outline"
                onClick={() => setBillDialogOpen(true)}
                className="rounded-[10px]"
              >
                <Receipt className="h-4 w-4" /> Bill this lesson
              </Button>
            )}
            {isSent && isBilled && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#e6f4ea] px-2.5 py-1 text-[11px] font-semibold text-[#054d28] dark:bg-[#054d28]/30 dark:text-[#7ed492]">
                <Receipt className="h-3 w-3" /> Billed
              </span>
            )}
            {!isSent && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="More actions"
                    className="rounded-[10px]"
                  >
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={() => setConfirmDiscard(true)}
                    className="text-destructive focus:text-destructive"
                  >
                    <Trash2 className="mr-2 h-4 w-4" /> Discard recap
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Discard this recap?</AlertDialogTitle>
              <AlertDialogDescription>
                The lesson with {clientName} stays in your records and on the client's history —
                you just won't send a recap email. You can undo this for a few seconds.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDiscard}>Discard</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {client && user && (
          <BillLessonDialog
            open={billDialogOpen}
            onOpenChange={setBillDialogOpen}
            sessionId={sessionId}
            coachId={user.id}
            clientId={client.id}
            clientName={client.student_name ?? "client"}
            defaultRateCents={client.default_rate_cents ?? 0}
            attendees={attendees.map((a) => ({
              id: a.id,
              coach_student_id: a.coach_student_id,
              attended: a.attended,
            }))}
            policy={
              (coachProfile?.group_lesson_credit_policy as GroupCreditPolicy | undefined) ??
              "each_attendee_burns_one"
            }
            onBilled={() => {
              setIsBilled(true);
              load();
            }}
          />
        )}




        {/* Attendees (group session) */}
        {attendees.length >= 2 && (
          <div className="mb-4 rounded-[16px] border bg-card p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-sm font-bold tracking-tight">
                  Attendees · {attendees.length}
                </h2>
                <p className="text-[11px] text-muted-foreground">
                  Uncheck anyone who didn't show. Notes are private to you.
                </p>
              </div>
              <label className="flex items-center gap-2 text-xs">
                <Switch
                  checked={sendIndividualRecaps}
                  onCheckedChange={toggleSendIndividualRecaps}
                  aria-label="Send individual recaps"
                />
                <span className="font-semibold">
                  Send individual recaps
                </span>
              </label>
            </div>
            <ul className="mt-3 space-y-2">
              {attendees.map((a) => (
                <li
                  key={a.id}
                  className="flex flex-col gap-2 rounded-[12px] border px-3.5 py-3 sm:flex-row sm:items-start"
                >
                  <label className="flex items-center gap-2 sm:w-48 sm:shrink-0">
                    <Checkbox
                      checked={a.attended}
                      onCheckedChange={(v) => toggleAttendeeAttended(a.id, v === true)}
                    />
                    <span
                      className={`text-sm font-semibold ${a.attended ? "" : "text-muted-foreground line-through"}`}
                    >
                      {a.student_name}
                    </span>
                  </label>
                  <div className="flex flex-1 flex-col gap-2">
                    <Textarea
                      defaultValue={a.per_attendee_note}
                      onBlur={(e) => saveAttendeeNote(a.id, e.target.value)}
                      rows={2}
                      placeholder={`Private note for ${a.student_name}…`}
                      className="resize-none rounded-[10px] text-sm"
                    />
                    {sendIndividualRecaps && a.attended && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[11px] text-muted-foreground">
                          {a.recap_sent_at
                            ? `Recap sent ${new Date(a.recap_sent_at).toLocaleString()}`
                            : a.student_email
                              ? a.student_email
                              : "No email on file"}
                        </span>
                        <Button
                          size="sm"
                          variant={a.recap_sent_at ? "outline" : "default"}
                          disabled={!draftText.trim() || !a.student_email}
                          onClick={() => sendAttendeeRecap(a)}
                          className="rounded-[10px]"
                        >
                          <Send className="h-3.5 w-3.5" />
                          {a.recap_sent_at ? "Resend" : "Send recap"}
                        </Button>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Two-column body */}
        <div className="grid gap-4 lg:grid-cols-[1fr_1.4fr]">
          {/* Left card: What we worked on */}
          <div className="rounded-[16px] border bg-card p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-bold tracking-tight">What we worked on</h2>
              <button
                type="button"
                onClick={() => toast.info("Add drills from the Log session screen.")}
                className="inline-flex items-center gap-1 rounded-md px-1.5 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground"
              >
                <Plus className="h-3.5 w-3.5" /> Add drill
              </button>
            </div>

            <ul className="mt-3 space-y-2">
              {items.length === 0 && (
                <li className="rounded-[12px] border border-dashed px-4 py-6 text-center text-xs text-muted-foreground">
                  No drills logged for this session.
                </li>
              )}
              {items.map((i) => (
                <li
                  key={i.id}
                  className={`flex items-start gap-3 rounded-[12px] border px-3.5 py-3 ${
                    i.is_homework
                      ? "bg-[rgba(159,232,112,0.05)] dark:bg-[rgba(159,232,112,0.06)]"
                      : "bg-card"
                  }`}
                >
                  <span
                    aria-hidden
                    className={`mt-0.5 flex h-[18px] w-[18px] shrink-0 items-center justify-center rounded-[5px] border ${
                      i.completed
                        ? "border-[#2ead4b] bg-[#9fe870] text-[#054d28]"
                        : "border-border bg-card"
                    }`}
                  >
                    {i.completed && (
                      <svg viewBox="0 0 20 20" fill="none" className="h-3 w-3">
                        <path
                          d="M5 10.5l3 3 7-7"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold">{i.title}</p>
                    {i.body_text && (
                      <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
                        {i.body_text}
                      </p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {i.is_homework && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-court/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-court">
                        <Home className="h-3 w-3" /> Homework
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => toast.info("Edit drills from the Log session screen.")}
                      aria-label="Edit drill"
                      className="flex h-7 w-7 items-center justify-center rounded-[7px] border text-muted-foreground hover:text-foreground"
                    >
                      <Pencil className="h-3 w-3" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>

            <div className="mt-5">
              <label className="text-xs font-semibold text-muted-foreground">Session notes</label>
              <Textarea
                value={sessionNotes}
                onChange={(e) => setSessionNotes(e.target.value)}
                onBlur={saveSessionNotes}
                rows={4}
                placeholder="Add private notes for next time…"
                className="mt-1.5 resize-none rounded-[10px] text-sm"
              />
            </div>
          </div>

          {/* Right card: Recap email (dark) */}
          <div className="overflow-hidden rounded-[16px] bg-[#0e0f0c] p-6 text-white">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#9fe870]" />
                <h2 className="text-sm font-bold tracking-tight text-white">Recap email</h2>
                {hasDraft && (
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-semibold text-white/90">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#9fe870] opacity-75" />
                      <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#9fe870]" />
                    </span>
                    Automated draft
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={generateDraft}
                disabled={generating}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-white/70 hover:text-white disabled:opacity-50"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${generating ? "animate-spin" : ""}`} />
                {generating ? "Drafting…" : "Regenerate"}
              </button>
            </div>

            {/* Email mock */}
            <div className="mt-4 rounded-[12px] border border-white/10 bg-[#0a0b08] p-5">
              <p className="text-[11px] text-white/50">To: {client?.student_email ?? "—"}</p>
              <p className="mt-1 text-[15px] font-bold text-white">{subject}</p>
              <div className="my-3 h-px bg-white/10" />
              {!hasDraft ? (
                <div className="py-6 text-center">
                  <p className="text-sm text-white/60">
                    {generating
                      ? "Drafting your recap…"
                      : "No draft yet. Click Regenerate to create a personalized recap."}
                  </p>
                </div>

              ) : editingDraft ? (
                <Textarea
                  value={draftText}
                  onChange={(e) => setDraftText(e.target.value)}
                  rows={14}
                  autoFocus
                  className="min-h-[300px] resize-none border-white/10 bg-transparent font-sans text-[13px] leading-[1.65] text-white/90 placeholder:text-white/30 focus-visible:ring-white/20"
                />
              ) : (
                <pre className="whitespace-pre-wrap break-words font-sans text-[13px] leading-[1.65] text-white/[0.88]">
{draftText}
                </pre>
              )}
            </div>

            {/* Actions */}
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <Button
                onClick={emailRecap}
                disabled={sending || !draftText.trim() || !client?.student_email}
                className="rounded-[10px] bg-[#9fe870] text-[#0e0f0c] hover:bg-[#a8eb7e]"
              >
                <Send className="h-4 w-4" /> Send to {firstName}
              </Button>
              <Button
                type="button"
                onClick={async () => {
                  if (editingDraft) await saveDraft();
                  setEditingDraft((v) => !v);
                }}
                className="rounded-[10px] border-0 bg-white/10 text-white hover:bg-white/15"
              >
                <Pencil className="h-4 w-4" /> {editingDraft ? "Done" : "Edit draft"}
              </Button>
              <p className="ml-auto text-[11px] text-white/50">
                Includes {drillCount} drill{drillCount === 1 ? "" : "s"} · {homeworkCount} homework
                · {clips.length} clip{clips.length === 1 ? "" : "s"}
              </p>
            </div>
          </div>
        </div>

        {/* Clips are managed on the Log Session screen — they're loaded here only to embed in the recap email. */}
      </div>

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl p-0 sm:max-w-2xl [&>button.absolute]:hidden">
          <DialogHeader className="border-b px-5 py-3 pr-12">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <DialogTitle className="text-base">Email preview</DialogTitle>
                <p className="truncate text-xs text-muted-foreground">
                  To: {client?.student_email ?? "—"} · Subject: Lesson recap from{" "}
                  {user?.user_metadata?.full_name ?? "Your coach"}
                </p>
              </div>
              <div className="inline-flex shrink-0 rounded-md border bg-muted p-0.5">
                <button
                  type="button"
                  onClick={() => setPreviewDevice("desktop")}
                  className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors ${previewDevice === "desktop" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                  aria-pressed={previewDevice === "desktop"}
                >
                  <Monitor className="h-3.5 w-3.5" /> Desktop
                </button>
                <button
                  type="button"
                  onClick={() => setPreviewDevice("mobile")}
                  className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium transition-colors ${previewDevice === "mobile" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
                  aria-pressed={previewDevice === "mobile"}
                >
                  <Smartphone className="h-3.5 w-3.5" /> Mobile
                </button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex h-[70vh] w-full items-start justify-center overflow-auto bg-muted p-4">
            {previewLoading ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                Rendering preview…
              </div>
            ) : (
              <iframe
                title="Email preview"
                srcDoc={previewHtml}
                className="h-full border-0 bg-white shadow-sm transition-[width] duration-200"
                style={{
                  width: previewDevice === "mobile" ? "375px" : "100%",
                  maxWidth: "100%",
                }}
                sandbox=""
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
